import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import tripRequestLogo from "../../../assets/images/trip_request_icon.svg";
import TopHeader from "../../../components/elements/TopHeader";
import "../css/TripRequestHeader.css";
import { BellFilled, ExportOutlined } from "@ant-design/icons";
import ROUTE_PATHS from "../../../utils/routePaths";
import useLandingAuthApi from "../../../services/api/useLandingAuthApi";
import { useFormData } from "../../../helper/FormDataProvider";

const Header = ({
  onExportRequest,
  isExportPending = false,
  isExportDisabled = true,
}) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const navigate = useNavigate();
  const { userData } = useFormData();
  const { fetchInAppNotification, markNotificationAsRead, fetchUnreadNotificationCount } =
    useLandingAuthApi();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const UNREAD_NOTIFICATION_COUNT_STORAGE_KEY = "unreadNotificationCount";
  const [unreadCount, setUnreadCount] = useState(() => {
    const stored = localStorage.getItem(
      UNREAD_NOTIFICATION_COUNT_STORAGE_KEY
    );
    const parsed = stored !== null ? Number(stored) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  });

  const loadUnreadCount = async () => {
    try {
      const response = await fetchUnreadNotificationCount(userData?.data?.id);
      if (response?.data !== undefined && response?.data !== null) {
        setUnreadCount(response.data);
        localStorage.setItem(
          UNREAD_NOTIFICATION_COUNT_STORAGE_KEY,
          String(response.data)
        );
      }
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  useEffect(() => {
    if (userData?.data?.id) {
      loadUnreadCount();
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [userData?.data?.id]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await fetchInAppNotification(0, 4);
      if (response?.data) {
        const notificationsData = response.data.content
          .map((notification) => ({
            id: notification.id,
            message: notification.notification,
            read: notification.isRead,
            itemId: notification.itemId,
            serviceType: notification.serviceType,
          }))
          .sort((a, b) => {
            if (a.read !== b.read) {
              return a.read ? 1 : -1;
            }
            return 0;
          });

        setNotifications(notificationsData);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showNotifications) {
      loadNotifications();
    }
  }, [showNotifications]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );

      setUnreadCount((prevCount) => {
        const nextValue = Math.max(0, prevCount - 1);
        localStorage.setItem(
          UNREAD_NOTIFICATION_COUNT_STORAGE_KEY,
          String(nextValue)
        );
        return nextValue;
      });
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleViewAll = () => {
    setShowNotifications(false);
    navigate(ROUTE_PATHS.NOTIFICATIONS_PAGE);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await handleMarkAsRead(notification.id);
    }
    if (notification.serviceType === "TRIP" && notification.itemId) {
      navigate(`${ROUTE_PATHS.TRIP_DETAILS(notification.itemId)}`, {
        state: { tripId: notification.itemId },
      });
    }
    setShowNotifications(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const notificationContainer = document.querySelector('.trip-request-notification-container');
      if (notificationContainer && !notificationContainer.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  return (
    <header>
      <TopHeader />
      <div className="trip-requests-container">
        <div className="trip-request-left-content">
          <img src={tripRequestLogo} className="trip-request-logo" alt="logo" />
          <span className="trip-request-text">Trip Requests</span>
        </div>
        <div className="trip-request-header-actions">
          <button
            type="button"
            className={`trip-request-header-export-button ${isExportPending || isExportDisabled ? "trip-request-header-export-button-disabled" : ""
              }`}
            onClick={onExportRequest}
            disabled={isExportPending || isExportDisabled}
          >
            <ExportOutlined className="trip-request-header-export-button-icon" />
            {isExportPending ? "Exporting..." : "Export"}
          </button>
        </div>
        <div className="trip-request-notification-container">
          <div
            className="trip-request-bell-icon"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <BellFilled className="trip-request-bell-icon-svg" />
            {unreadCount > 0 && (
              <span className="trip-request-notification-badge">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </div>
          {showNotifications && (
            <div className="trip-request-notifications-dropdown">
              {loading ? (
                <div className="trip-request-notification-loading">
                  Loading...
                </div>
              ) : notifications.length === 0 ? (
                <div className="trip-request-empty-notification">
                  <div className="trip-request-empty-notification-message">
                    No new notifications
                  </div>
                  <div className="trip-request-empty-notification-subtext">
                    You're all caught up!
                  </div>
                </div>
              ) : (
                <>
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`trip-request-notification-item ${!notification.read ? "trip-request-unread" : ""
                        }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="trip-request-notification-content">
                        <div className="trip-request-notification-message">
                          {notification.message}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="trip-request-notification-footer">
                    <span
                      className="trip-request-view-all"
                      onClick={handleViewAll}
                    >
                      View all
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;